import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, toDropdownFieldMeta } from "./formFieldHelpers";
import DropdownField, { DropdownOption } from "../../../common/formFields/DropdownField";
import { generatePickerOptions } from "../../../common/utils/appUtils";

/**
 * Shared "On Behalf Of" field (Spec #35289, AC1). Ports the Classic Audit(53)
 * control (DataEntryDetailsRightPane.tsx:2121-2133) into Quick Entry as a plain
 * DropdownField, mirroring ClinicResponsibleField. Options come from the same
 * redux slice Classic reads - state.dataEntryState.onBehalfOf ({ titleText, oboid }),
 * fetched once by getOBOsAPI.
 *
 * Manual-entry only (Assumption A1): the value is held in DATA_ENTRY_FIELDS_FORM
 * and NOT persisted until Confirm All - this component only writes to the form.
 */
/**
 * The shape state.dataEntryState.onBehalfOf holds - the same object Classic feeds to
 * pickerHelperFunction(props.onBehalfOf, "titleText", "oboid"). Rows may carry extra
 * server fields; only the two this component reads are declared.
 */
export interface OnBehalfOfOption {
    readonly titleText: string;
    readonly oboid: number;
}

export interface OnBehalfOfFieldProps {
    /** Currently selected on-behalf-of id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** state.dataEntryState.onBehalfOf - option source, built the same way Classic does. */
    readonly onBehalfOf?: readonly OnBehalfOfOption[];
    readonly dispatch: Function;
    readonly formName?: string;
    /** Non-form side effect the caller runs on change (Quick Entry marks the row as edited). */
    readonly onOnBehalfOfChange?: (newValue: number) => void;
    /** Real redux-form validation state, threaded down by Classic; omitted by Quick Entry. */
    readonly meta?: ClassicFieldMeta;
}

const OnBehalfOfField: React.FC<OnBehalfOfFieldProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const options: readonly DropdownOption[] = generatePickerOptions(props.onBehalfOf, "titleText", "oboid")
        .map(option => ({ label: option.label, value: option.value }));

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.ON_BEHALF_OF, newValue, formName);
        props.onOnBehalfOfChange?.(newValue);
    };

    return (
        <DropdownField
            label="On Behalf Of"
            required
            meta={toDropdownFieldMeta(props.meta)}
            options={options}
            onChange={onChange}
            value={props.value}
        />
    );
};

export default OnBehalfOfField;

export const ON_BEHALF_OF_ROW_ID = "onBehalfOf";

/**
 * Builds the FieldGroupRow for the On Behalf Of row. Always required when visible
 * (only rendered for Audit(53)); same positive-id NeedsEntry rule as AuditTypeField
 * so a blank value blocks Confirm All (AC3).
 */
export function mapOnBehalfOfToFieldGroupRow(badgeNumber: number, formValue: number | undefined, onBehalfOf?: readonly OnBehalfOfOption[]): FieldGroupRow {
    const numericValue = Number(formValue);
    const hasValue = formValue !== undefined && formValue !== null && (formValue as any) !== ""
        && Number.isFinite(numericValue) && numericValue > 0;
    const list: readonly any[] = Array.isArray(onBehalfOf)
        ? onBehalfOf
        : onBehalfOf ? Object.values(onBehalfOf) : [];
    const matched = hasValue
        ? list.find((obo: any) => Number(obo?.oboid) === numericValue)
        : undefined;

    return {
        id: ON_BEHALF_OF_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "On Behalf Of",
        value: matched?.titleText ?? (hasValue ? String(formValue) : null),
        isAiPreSelected: false,
    };
}
